<template>
  <div class="form-card-light">
    <!-- Main content -->
    <div class="text-center mb-5">
      <div class="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full mb-3 shadow-xl">
        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
      </div>
      <h2 class="text-2xl sm:text-3xl font-bold mb-2 text-gray-900">{{ $t('evidenceUpload.title') }}</h2>
      <p class="text-gray-600">{{ $t('evidenceUpload.subtitle') }}</p>
    </div>

    <div class="space-y-6">
      <!-- Evidence Upload Section -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
          </svg>
          {{ $t('evidenceUpload.sectionTitle') }}
        </h3>
        
        <!-- Drop Zone -->
        <div 
          @click="triggerFileInput"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
          :class="['upload-dropzone', isDragging ? 'dropzone-active' : '']"
        >
          <input 
            ref="evidenceInput"
            type="file" 
            multiple
            accept="image/*,.pdf,.doc,.docx,.txt"
            @change="handleUpload"
            class="hidden"
          />
          <div class="text-center">
            <svg class="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
            </svg>
            <p class="text-gray-600 mb-2">{{ $t('evidenceUpload.dropzone') }}</p>
            <button type="button" class="btn-choose-files">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              {{ $t('evidenceUpload.chooseFiles') }}
            </button>
            <p class="text-xs text-gray-500 mt-3">{{ $t('evidenceUpload.fileHint') }}</p>
          </div>
        </div>

        <!-- File List -->
        <div v-if="localEvidenceFiles.length > 0" class="mt-4 space-y-2">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">{{ $t('evidenceUpload.filesSelected', { count: localEvidenceFiles.length }) }}</span>
            <button @click="clearAllFiles" class="text-sm text-red-600 hover:text-red-700">{{ $t('evidenceUpload.clearAll') }}</button>
          </div>
          <div v-for="(file, index) in localEvidenceFiles" :key="index" class="file-item flex items-center justify-between">
            <div class="flex items-center gap-3 min-w-0">
              <svg class="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <div class="min-w-0">
                <p class="text-sm font-medium text-gray-700 truncate">{{ file.name }}</p>
                <p class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</p>
              </div>
            </div>
            <button @click="removeFile(index)" class="p-1 text-red-500 hover:bg-red-50 rounded">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- File Error -->
        <p v-if="fileError" class="error-message mt-3">
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          {{ fileError }}
        </p>
      </div>
    </div>

    <!-- Global Error Display -->
    <div v-if="globalError" class="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center">
      <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      {{ globalError }}
    </div>

    <!-- Navigation Buttons -->
    <div class="flex flex-col sm:flex-row gap-4 pt-8 justify-center items-center">
      <button 
        type="button"
        @click="$emit('back')"
        class="btn-secondary group w-full sm:w-auto"
      >
        <svg class="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12"></path>
        </svg>
        {{ $t('evidenceUpload.backBtn') }}
      </button>
      
      <button 
        type="button"
        @click="generatePDFPreview"
        class="btn-primary group w-full sm:w-auto"
        :disabled="generatingPDF"
      >
        <svg v-if="generatingPDF" class="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
        </svg>
        <svg v-else class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
        <span>{{ generatingPDF ? $t('evidenceUpload.generatingBtn') : $t('evidenceUpload.previewBtn') }}</span>
      </button>
    </div>

    <!-- ==================== PDF PREVIEW MODAL ==================== -->
    <teleport to="body">
      <transition name="modal">
        <div v-if="showPDFPreview" class="modal-overlay" @click.self="!submittingPDF && closePDFPreview()">
          <div class="modal-container">
            <!-- Modal Header -->
            <div class="modal-header">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">{{ $t('evidenceUpload.modal.title') }}</h3>
                </div>
              </div>
              <button 
                @click="closePDFPreview" 
                class="modal-close-btn"
                :disabled="submittingPDF"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <!-- Modal Body -->
            <div class="modal-body">
              <!-- Submission Progress Overlay -->
              <div v-if="showSubmissionProgress" class="submission-overlay">
                <div class="submission-content">
                  <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <svg class="w-8 h-8 text-green-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                  </div>
                  <h4 class="text-lg font-semibold text-gray-900 mb-2">{{ $t('evidenceUpload.modal.submittingTitle') }}</h4>
                  <p class="text-sm text-gray-600 mb-6">{{ $t('evidenceUpload.modal.submittingMessage') }}</p>
                  
                  <!-- Progress Steps -->
                  <div class="space-y-3 text-left max-w-xs mx-auto mb-6">
                    <div class="flex items-center gap-3">
                      <div :class="['w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium', submissionStep >= 1 ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500']">
                        <span v-if="submissionStep > 1">✓</span>
                        <span v-else>1</span>
                      </div>
                      <span class="text-sm text-gray-700">{{ $t('evidenceUpload.modal.step1') }}</span>
                      <svg v-if="submissionStep === 1" class="w-4 h-4 text-green-500 animate-spin ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                    </div>
                    <div class="flex items-center gap-3">
                      <div :class="['w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium', submissionStep >= 2 ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500']">
                        <span v-if="submissionStep > 2">✓</span>
                        <span v-else>2</span>
                      </div>
                      <span class="text-sm text-gray-700">{{ $t('evidenceUpload.modal.step2') }}</span>
                      <svg v-if="submissionStep === 2" class="w-4 h-4 text-green-500 animate-spin ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                    </div>
                    <div class="flex items-center gap-3">
                      <div :class="['w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium', submissionStep >= 3 ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500']">
                        <span v-if="submissionStep > 3">✓</span>
                        <span v-else>3</span>
                      </div>
                      <span class="text-sm text-gray-700">{{ $t('evidenceUpload.modal.step3') }}</span>
                      <svg v-if="submissionStep === 3" class="w-4 h-4 text-green-500 animate-spin ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                    </div>
                  </div>
                  
                  <!-- Progress Bar -->
                  <div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div class="h-full bg-green-500 rounded-full transition-all duration-300" :style="{ width: submissionProgress + '%' }"></div>
                  </div>
                  <p class="text-xs text-gray-500 mt-2">{{ Math.round(submissionProgress) }}%</p>
                </div>
              </div>

              <!-- Loading State -->
              <div v-else-if="pdfLoading" class="pdf-state-container">
                <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
                  <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
                <p class="text-gray-700 font-medium">{{ $t('evidenceUpload.modal.loadingDoc') }}</p>
                <div class="w-48 h-1.5 bg-gray-200 rounded-full mt-4 overflow-hidden">
                  <div class="h-full bg-blue-500 rounded-full animate-progress"></div>
                </div>
              </div>

              <!-- Error State -->
              <div v-else-if="pdfError && !pdfBlobUrl" class="pdf-state-container">
                <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                </div>
                <p class="text-gray-900 font-medium mb-2">{{ $t('evidenceUpload.modal.errorTitle') }}</p>
                <p class="text-sm text-gray-500 mb-4">{{ pdfError }}</p>
                <button @click="downloadPDF" :disabled="downloadingPDF" class="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                  </svg>
                  {{ downloadingPDF ? $t('evidenceUpload.modal.downloadingBtn') : $t('evidenceUpload.modal.downloadInstead') }}
                </button>
              </div>

              <!-- PDF Preview - MOBILE -->
              <div v-else-if="pdfBlobUrl && isMobile" class="pdf-state-container">
                <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg class="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h4 class="text-xl font-bold text-gray-900 mb-2">{{ $t('evidenceUpload.modal.pdfReadyTitle') }}</h4>
                <p class="text-gray-600 mb-6 text-sm">{{ $t('evidenceUpload.modal.pdfReadyMessage') }}</p>
                
                <!-- Form Summary -->
                <div class="bg-blue-50 p-4 rounded-xl border border-blue-100 text-left mb-6 w-full max-w-sm">
                  <h5 class="font-semibold mb-2 text-sm text-gray-800">{{ $t('evidenceUpload.modal.summaryTitle') }}</h5>
                  <p class="text-xs text-gray-600"><strong>{{ $t('evidenceUpload.modal.summaryName') }}</strong> {{ form.name || 'N/A' }}</p>
                  <p class="text-xs text-gray-600"><strong>{{ $t('evidenceUpload.modal.summaryType') }}</strong> {{ form.incident_type || 'N/A' }}</p>
                  <p class="text-xs text-gray-600"><strong>{{ $t('evidenceUpload.modal.summaryDate') }}</strong> {{ form.incident_date || 'N/A' }}</p>
                  <p v-if="reportReference" class="text-xs text-gray-600"><strong>{{ $t('evidenceUpload.modal.summaryRef') }}</strong> {{ reportReference }}</p>
                </div>

                <!-- Action Buttons for Mobile -->
                <div class="flex flex-col gap-3 w-full max-w-sm">
                  <button 
                    @click="openPdfInNewTab" 
                    class="w-full bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                    </svg>
                    {{ $t('evidenceUpload.modal.viewPdfBtn') }}
                  </button>
                  <button 
                    @click="downloadPdfBlob" 
                    class="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 border border-gray-300"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                    </svg>
                    {{ $t('evidenceUpload.modal.downloadPdfBtn') }}
                  </button>
                </div>
              </div>

              <!-- PDF Preview - DESKTOP -->
              <div v-else-if="pdfBlobUrl && !isMobile" class="pdf-preview-container">
                <iframe 
                  :src="pdfBlobUrl"
                  class="pdf-iframe"
                  @load="onPDFLoad"
                  @error="onPDFError"
                  title="PDF Preview"
                ></iframe>
              </div>

              <!-- Success State (No PDF URL) -->
              <div v-else class="pdf-state-container">
                <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <p class="text-gray-900 font-medium mb-2">{{ $t('evidenceUpload.modal.docGeneratedTitle') }}</p>
                <p class="text-sm text-gray-500 mb-1">{{ $t('evidenceUpload.modal.docGeneratedMessage') }}</p>
                <p v-if="reportReference" class="text-xs text-gray-400">{{ $t('evidenceUpload.modal.summaryRef') }} {{ reportReference }}</p>
                <button @click="downloadPDF" :disabled="downloadingPDF" class="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 mx-auto">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                  </svg>
                  {{ downloadingPDF ? $t('evidenceUpload.modal.downloadingBtn') : $t('evidenceUpload.modal.downloadPdfBtn') }}
                </button>
              </div>
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer">
              <button 
                @click="rejectAndClose" 
                :disabled="submittingPDF"
                class="modal-btn modal-btn-secondary"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                {{ $t('evidenceUpload.modal.cancelBtn') }}
              </button>
              
              <button 
                @click="confirmPDFSubmission" 
                :disabled="submittingPDF || pdfLoading"
                class="modal-btn modal-btn-primary"
              >
                <svg v-if="submittingPDF" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                {{ submittingPDF ? $t('evidenceUpload.modal.submittingBtn') : $t('evidenceUpload.modal.confirmBtn') }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import { generateSecureFileName, sanitizeFileName } from '@/utils/security-utils';
import getAPI from '@/axios-api';
import JSZip from 'jszip';

export default {
  name: 'EvidenceUploadWithPreview',
  props: {
    sessionUuid: {
      type: String,
      required: true
    },
    form: {
      type: Object,
      required: true,
      default: () => ({
        name: '',
        email: '',
        phone_number: '',
        profession: '',
        id_type: '',
        id_number: '',
        residence_address: '',
        residence_town: '',
        incident_type: '',
        incident_date: '',
        incident_description: '',
        otherIncident: ''
      })
    },
    evidenceFiles: {
      type: Array,
      default: () => []
    },
    initialIncidentId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['back', 'submit', 'update:evidenceFiles'],
  data() {
    return {
      isDragging: false,
      localEvidenceFiles: [],
      fileError: '',
      showPDFPreview: false,
      generatingPDF: false,
      pdfLoading: false,
      pdfError: null,
      submittingPDF: false,
      downloadingPDF: false,
      showSubmissionProgress: false,
      submissionProgress: 0,
      submissionStep: 0,
      pdfBlobUrl: null,
      pdfFileName: '',
      pdfObjectLoaded: false,
      incidentId: null,
      reportReference: '',
      accessToken: null,
      alreadySaved: false,
      globalError: ''
    };
  },
  computed: {
    isMobile() {
      if (typeof window === 'undefined') return false;
      const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i;
      const isSmallScreen = window.innerWidth < 768;
      return mobileRegex.test(userAgent.toLowerCase()) || isSmallScreen;
    }
  },
  watch: {
    evidenceFiles: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.localEvidenceFiles = [...newVal];
        }
      }
    },
    showPDFPreview(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    initialIncidentId: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.incidentId) {
          this.incidentId = newVal;
        }
      }
    }
  },
  mounted() {
    if (this.initialIncidentId) {
      this.incidentId = this.initialIncidentId;
    } else {
      const stored = localStorage.getItem('incident_id');
      if (stored) {
        this.incidentId = stored;
      }
    }
  },
  methods: {
    /**
     * Returns a locale-aware fallback PDF filename.
     */
    getFallbackFilename() {
      return this.$i18n.locale === 'fr' ? "Plainte_d'Usager_Antic.pdf" : 'Antic_Plaintiff_Complaint.pdf';
    },

    triggerFileInput() {
      this.$refs.evidenceInput.click();
    },
    
    handleUpload(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
      event.target.value = '';
    },
    
    handleDragOver() {
      this.isDragging = true;
    },
    
    handleDragLeave() {
      this.isDragging = false;
    },
    
    handleDrop(event) {
      this.isDragging = false;
      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },
    
    processFiles(files) {
      const maxSize = 25 * 1024 * 1024;
      const maxFiles = 10;
      let totalSize = 0;
      
      if (this.localEvidenceFiles.length + files.length > maxFiles) {
        this.fileError = this.$t('evidenceUpload.errors.maxFiles', { max: maxFiles });
        return;
      }
      
      this.localEvidenceFiles.forEach(file => {
        totalSize += file.size;
      });
      
      files.forEach(file => {
        totalSize += file.size;
      });
      
      if (totalSize > maxSize) {
        this.fileError = this.$t('evidenceUpload.errors.totalSize');
        return;
      }
      
      this.fileError = '';
      this.localEvidenceFiles = [...this.localEvidenceFiles, ...files];
      this.$emit('update:evidenceFiles', this.localEvidenceFiles);
    },
    
    removeFile(index) {
      this.localEvidenceFiles.splice(index, 1);
      this.$emit('update:evidenceFiles', this.localEvidenceFiles);
    },
    
    clearAllFiles() {
      this.localEvidenceFiles = [];
      this.$emit('update:evidenceFiles', []);
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },

    showGlobalError(message, duration = 5000) {
      this.globalError = message;
      if (duration > 0) {
        setTimeout(() => {
          if (this.globalError === message) {
            this.globalError = '';
          }
        }, duration);
      }
    },
    
    clearGlobalError() {
      this.globalError = '';
    },

    rejectAndClose() {
      if (confirm(this.$t('evidenceUpload.cancelConfirm'))) {
        this.closePDFPreview();
      }
    },

    openPdfInNewTab() {
      if (this.pdfBlobUrl) {
        const newWindow = window.open(this.pdfBlobUrl, '_blank');
        if (!newWindow) {
          this.downloadPdfBlob();
        }
      }
    },

    downloadPdfBlob() {
      if (this.pdfBlobUrl) {
        const link = document.createElement('a');
        link.href = this.pdfBlobUrl;
        link.download = this.pdfFileName || this.getFallbackFilename();
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    async generatePDFPreview() {
      this.generatingPDF = true;
      this.pdfLoading = true;
      this.pdfError = null;
      this.pdfBlobUrl = null;
      this.pdfObjectLoaded = false;
      this.alreadySaved = false;
      this.clearGlobalError();
      
      try {
        if (!this.sessionUuid) {
          throw new Error(this.$t('evidenceUpload.errors.sessionNotFound'));
        }

        const pdfGenerationData = {
          session_uuid: this.sessionUuid,
          custom_incident_type: this.form.otherIncident || '',
          location: this.form.residence_town || 'Yaoundé',
          preview: true
        };

        const response = await getAPI.post('/api/complaint-pdf/generate/', 
          pdfGenerationData, 
          {
            withCredentials: true,
            timeout: 120000
          }
        );

        if (!response.data || response.data.success === false) {
          throw new Error(response.data?.error || this.$t('evidenceUpload.errors.generateFailed'));
        }
        
        this.incidentId = response.data.incident_info?.incident_id || 
                          response.data.incident_id || 
                          (response.data.incident_info && response.data.incident_info.id);
        
        this.reportReference = response.data.c_reference_id || response.data.reference_id || '';
        
        this.accessToken = response.data.incident_info?.access_token || 
                          response.data.access_token || null;
        
        this.alreadySaved = !!response.data.already_saved;
        
        if (!this.incidentId) {
          throw new Error(this.$t('evidenceUpload.errors.missingIncidentId'));
        }
        
        localStorage.setItem('incident_id', this.incidentId);
        
        if (this.accessToken) {
          localStorage.setItem(`pdf_token_${this.incidentId}`, this.accessToken);
          localStorage.setItem('pdf_access_token', this.accessToken);
        }
        
        this.showPDFPreview = true;
        
        try {
          if (this.alreadySaved && response.data.download_url) {
            await this.fetchPDFFromPreviewUrl(response.data.download_url);
          } else if (response.data.preview?.download_url) {
            await this.fetchPDFFromPreviewUrl(response.data.preview.download_url);
          } else if (response.data.preview?.preview_url) {
            await this.fetchPDFFromPreviewUrl(response.data.preview.preview_url);
          } else if (this.incidentId && this.accessToken) {
            await this.fetchPDFForPreview();
          } else {
            const directUrl = `/api/complaint-pdf/preview/?incident_id=${this.incidentId}`;
            try {
              await this.fetchPDFFromPreviewUrl(directUrl);
            } catch (e) {
              this.pdfLoading = false;
              this.pdfError = this.$t('evidenceUpload.errors.previewUnavailable');
            }
          }
        } catch (fetchError) {
          this.pdfError = fetchError.message || this.$t('evidenceUpload.errors.previewFailed');
          this.pdfLoading = false;
        }
        
        this.showGlobalError(this.$t('evidenceUpload.pdfReady'), 3000);

      } catch (error) {
        this.showPDFPreview = true;
        
        if (error.response?.status === 404) {
          this.pdfError = this.$t('evidenceUpload.errors.error404');
        } else if (error.response?.status === 500) {
          this.pdfError = this.$t('evidenceUpload.errors.error500');
        } else if (error.message.includes('timeout')) {
          this.pdfError = this.$t('evidenceUpload.errors.timeout');
        } else if (error.message.includes('Network Error')) {
          this.pdfError = this.$t('evidenceUpload.errors.network');
        } else {
          this.pdfError = error.message || this.$t('evidenceUpload.errors.generateFailed');
        }
        
        this.showGlobalError(this.pdfError, 5000);
        
      } finally {
        this.generatingPDF = false;
        this.pdfLoading = false;
      }
    },

    async fetchPDFFromPreviewUrl(previewUrl) {
      
      const response = await getAPI.get(previewUrl, {
        responseType: 'blob',
        timeout: 60000,
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      if (response.data.size < 100) {
        throw new Error(this.$t('evidenceUpload.errors.invalidPdf'));
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      this.pdfBlobUrl = URL.createObjectURL(blob);
      
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          this.pdfFileName = filenameMatch[1];
        }
      }
      
      this.pdfObjectLoaded = true;
      this.pdfLoading = false;
    },

    async fetchPDFForPreview() {
      const id = this.incidentId;
      const token = this.accessToken;
      
      if (!id || !token) {
        throw new Error(this.$t('evidenceUpload.errors.missingIdOrToken'));
      }
      
      const encodedToken = encodeURIComponent(token);
      const url = `/api/complaint-pdf/preview/?incident_id=${id}&token=${encodedToken}`;
      
      const response = await getAPI.get(url, {
        responseType: 'blob',
        timeout: 60000,
        headers: {
          'Accept': 'application/pdf, */*'
        }
      });
      
      if (response.data.size < 100) {
        throw new Error(this.$t('evidenceUpload.errors.invalidPdf'));
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      this.pdfBlobUrl = URL.createObjectURL(blob);
      
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          this.pdfFileName = filenameMatch[1];
        }
      }
      
      this.pdfObjectLoaded = true;
      this.pdfLoading = false;
    },

    closePDFPreview() {
      if (this.submittingPDF || this.showSubmissionProgress) return;
      
      this.showPDFPreview = false;
      
      if (this.pdfBlobUrl) {
        URL.revokeObjectURL(this.pdfBlobUrl);
        this.pdfBlobUrl = null;
      }
      
      this.pdfLoading = false;
      this.pdfError = null;
      this.pdfObjectLoaded = false;
      this.showSubmissionProgress = false;
      this.submissionProgress = 0;
      this.submissionStep = 0;
    },

    onPDFLoad() {
      this.pdfObjectLoaded = true;
      this.pdfLoading = false;
    },

    onPDFError() {
      this.pdfError = this.$t('evidenceUpload.errors.iframeError');
      this.pdfLoading = false;
    },

    async downloadPDF() {
      this.downloadingPDF = true;
      
      try {
        if (!this.incidentId && !this.sessionUuid) {
          throw new Error(this.$t('evidenceUpload.errors.noIncidentInfo'));
        }
        
        const params = new URLSearchParams();
        if (this.incidentId) params.append('incident_id', this.incidentId);
        if (this.sessionUuid) params.append('session_uuid', this.sessionUuid);
        
        const url = `/api/complaint-pdf/download/?${params.toString()}`;
        
        const response = await getAPI.get(url, {
          responseType: 'blob',
          timeout: 60000
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        
        // Extract filename from Content-Disposition header
        let filename = null;
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match && match[1]) {
            filename = match[1].replace(/['"]/g, '');
          }
        }
        // Fallback: use stored name or locale-aware default
        if (!filename) {
          filename = this.pdfFileName || this.getFallbackFilename();
        }
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        
        this.showGlobalError(this.$t('evidenceUpload.downloadStarted'), 3000);
        
      } catch (error) {
        this.showGlobalError(this.$t('evidenceUpload.errors.downloadFailed') + (error.message || 'Unknown error'), 5000);
      } finally {
        this.downloadingPDF = false;
      }
    },

    async confirmPDFSubmission() {
      this.submittingPDF = true;
      this.showSubmissionProgress = true;
      this.submissionProgress = 10;
      this.submissionStep = 1;
      this.clearGlobalError();
      
      try {
        if (!this.incidentId) {
          throw new Error(this.$t('evidenceUpload.errors.missingIncidentInfo'));
        }
        
        if (this.alreadySaved) {
          this.submissionProgress = 50;
          this.submissionStep = 2;
          await this.submitEvidenceFiles();
          return;
        }
        
        let accessToken = this.accessToken || 
                          localStorage.getItem(`pdf_token_${this.incidentId}`) ||
                          localStorage.getItem('pdf_access_token');
        
        if (!accessToken) {
          throw new Error(this.$t('evidenceUpload.errors.missingToken'));
        }
        
        const confirmData = {
          incident_id: this.incidentId,
          session_uuid: this.sessionUuid,
          access_token: accessToken
        };
        
        this.submissionProgress = 30;
        
        const confirmResponse = await getAPI.post('/api/complaint-pdf/confirm/', 
          confirmData, 
          {
            withCredentials: true,
            timeout: 60000
          }
        );

        if (!confirmResponse.data.success) {
          throw new Error(confirmResponse.data.error || this.$t('evidenceUpload.errors.confirmFailed'));
        }

        this.reportReference = confirmResponse.data.c_reference_id || this.reportReference;
        
        localStorage.removeItem(`pdf_token_${this.incidentId}`);
        localStorage.removeItem('pdf_access_token');
        this.accessToken = null;
        
        this.submissionProgress = 50;
        this.submissionStep = 2;
        
        if (!confirmResponse.data.already_saved) {
          await this.submitEvidenceFiles();
        } else {
          this.submissionProgress = 80;
          this.submissionStep = 3;
          
          setTimeout(() => {
            this.submissionProgress = 100;
            setTimeout(() => {
              this.showSubmissionProgress = false;
              this.closePDFPreview();
              this.$emit('submit', this.reportReference);
            }, 500);
          }, 1000);
        }
        
      } catch (error) {
        let errorMessage = this.$t('evidenceUpload.errors.confirmSubmitFailed');
        if (error.response?.data?.error) {
          errorMessage += error.response.data.error;
        } else if (error.message) {
          errorMessage += error.message;
        }
        
        this.pdfError = errorMessage;
        this.showGlobalError(errorMessage, 5000);
        this.showSubmissionProgress = false;
        
        if (errorMessage.includes('token') || errorMessage.includes('expired')) {
          localStorage.removeItem(`pdf_token_${this.incidentId}`);
          localStorage.removeItem('pdf_access_token');
          this.accessToken = null;
        }
        
      } finally {
        this.submittingPDF = false;
        this.submissionProgress = 0;
        this.submissionStep = 0;
      }
    },

    async submitEvidenceFiles() {
      
      const formData = new FormData();
      formData.append('session_uuid', this.sessionUuid);

      if (this.localEvidenceFiles.length > 0) {
        const evidenceZip = new JSZip();
        
        this.submissionProgress = 60;
        
        for (let i = 0; i < this.localEvidenceFiles.length; i++) {
          evidenceZip.file(generateSecureFileName(this.localEvidenceFiles[i].name), this.localEvidenceFiles[i]);
          this.submissionProgress = 60 + Math.round((i / this.localEvidenceFiles.length) * 20);
        }
        
        this.submissionProgress = 80;
        
        const zipContent = await evidenceZip.generateAsync({ type: "blob" });
        const fileName = `${sanitizeFileName(this.form.name || 'user')}_evidence.zip`;
        formData.append('evidence_files', zipContent, fileName);
      }
      
      formData.append('step', 4);

      this.submissionProgress = 85;
      
      const response = await getAPI.post('/api/step/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        timeout: 480000
      });

      this.reportReference = response.data.reference_number || 
                            response.data.reference || 
                            this.reportReference;
      
      this.submissionProgress = 90;
      this.submissionStep = 3;
      
      setTimeout(() => {
        this.submissionProgress = 100;
        setTimeout(() => {
          this.showSubmissionProgress = false;
          this.closePDFPreview();
          this.$emit('submit', this.reportReference);
        }, 500);
      }, 1000);
    }
  },

  beforeUnmount() {
    document.body.style.overflow = '';
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
    }
  }
}
</script>

<style scoped>
/* Loading animation */
@keyframes progress {
  0% { width: 0%; margin-left: 0; }
  50% { width: 70%; margin-left: 15%; }
  100% { width: 0%; margin-left: 100%; }
}

.animate-progress {
  animation: progress 1.5s ease-in-out infinite;
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

/* Modal Container */
.modal-container {
  position: relative;
  width: 100%;
  height: 80vh; 
  max-width: 1200px;
  max-height: 150vh;
  background: white;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Modal Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.modal-close-btn {
  padding: 0.5rem;
  border-radius: 8px;
  color: #6b7280;
  transition: all 0.2s;
}

.modal-close-btn:hover:not(:disabled) {
  background: #f3f4f6;
  color: #374151;
}

.modal-close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal Body */
.modal-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  position: relative;
}

/* PDF State Container */
.pdf-state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  min-height: 300px;
}

/* PDF Preview Container */
.pdf-preview-container {
  width: 100%;
  height: 60vh;
  min-height: 400px;
  background: #1f2937;
  padding: 1rem;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  background: white;
}

/* Submission Overlay */
.submission-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.98);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.submission-content {
  text-align: center;
  padding: 2rem;
  max-width: 320px;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

/* Modal Buttons */
.modal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 10px;
  transition: all 0.2s;
}

.modal-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.modal-btn-secondary:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.modal-btn-primary {
  background: linear-gradient(to right, #10b981, #059669);
  color: white;
  border: none;
  box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.3);
}

.modal-btn-primary:hover:not(:disabled) {
  background: linear-gradient(to right, #059669, #047857);
  box-shadow: 0 6px 10px -1px rgba(16, 185, 129, 0.4);
  transform: translateY(-1px);
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95);
  opacity: 0;
}

/* Component styles */
.form-card-light {
  @apply bg-white/90 backdrop-blur-xl rounded-2xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-gray-200/50;
}

.upload-dropzone {
  @apply border-2 border-dashed border-gray-300 rounded-2xl p-8 bg-white/50
         transition-all duration-300 hover:border-blue-500 hover:bg-blue-50/50
         flex flex-col items-center justify-center cursor-pointer;
}

.dropzone-active {
  @apply border-blue-500 bg-blue-50 scale-[1.02];
}

.btn-choose-files {
  @apply bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2.5 px-5 rounded-xl
         shadow-md hover:shadow-lg transition-all duration-200 border border-gray-300
         inline-flex items-center;
}

.file-item {
  @apply p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-blue-50/50
         transition-all duration-200;
}

.error-message {
  @apply text-red-600 text-sm flex items-center justify-center;
}

.btn-primary {
  @apply bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700
         text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl
         transition-all duration-300 flex items-center justify-center disabled:opacity-50
         disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98];
}

.btn-secondary {
  @apply bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-xl
         shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center
         border border-gray-300 transform hover:scale-[1.02] active:scale-[0.98];
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .modal-overlay {
    padding: 0.5rem;
  }
  
  .modal-container {
    max-height: 95vh;
    border-radius: 12px;
  }
  
  .modal-header {
    padding: 0.875rem 1rem;
  }
  
  .modal-footer {
    padding: 0.875rem 1rem;
    flex-direction: column-reverse;
  }
  
  .modal-btn {
    width: 100%;
  }
  
  .pdf-preview-container {
    height: 50vh;
    min-height: 300px;
    padding: 0.5rem;
  }
  
  .pdf-state-container {
    padding: 2rem 1rem;
  }
}
</style>